import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { expensesApi } from '@/services/accountingService';
import { bcadd, bctoFixed } from "@/hooks/useBcmath";

interface ExpensesState {
  expenses: any[];
  expense: any | null;
  loading: boolean;
  error: any | null;
}

const initialState: ExpensesState = {
  expenses: [],
  expense: null,
  loading: false,
  error: null,
};

export const fetchExpenses = createAsyncThunk(
  'expenses/fetchExpenses',
  async (params: Record<string, any> | undefined, { rejectWithValue }) => {
    try {
      const response: any = await expensesApi.getAll(params || {});
      return response.data.data || response.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const fetchExpense = createAsyncThunk(
  'expenses/fetchExpense',
  async (id: number | string, { rejectWithValue }) => {
    try {
      const response: any = await expensesApi.getById(id);
      return response.data.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const createExpense = createAsyncThunk(
  'expenses/createExpense',
  async (data: any, { rejectWithValue }) => {
    try {
      const response: any = await expensesApi.create(data);
      return response.data.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const updateExpense = createAsyncThunk(
  'expenses/updateExpense',
  async ({ id, data }: { id: number | string; data: any }, { rejectWithValue }) => {
    try {
      const response: any = await expensesApi.update(id, data);
      return response.data.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const deleteExpense = createAsyncThunk(
  'expenses/deleteExpense',
  async (id: number | string, { rejectWithValue }) => {
    try {
      await expensesApi.delete(id);
      return id;
    } catch (err: any) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

const expensesSlice = createSlice({
  name: 'expenses',
  initialState,
  reducers: {
    clearExpense(state) {
      state.expense = null;
    },
    resetExpenses(state) {
      Object.assign(state, initialState);
    },
  },
  extraReducers: (builder) => {
    const handlePending = (state: ExpensesState) => {
      state.loading = true;
      state.error = null;
    };
    const handleRejected = (state: ExpensesState, action: any) => {
      state.loading = false;
      state.error = action.payload;
    };

    builder
      .addCase(fetchExpenses.pending, handlePending)
      .addCase(fetchExpenses.fulfilled, (state, action) => {
        state.loading = false;
        // Validate: a non-array payload here broke every downstream .map/.length.
        state.expenses = Array.isArray(action.payload) ? action.payload : [];
      })
      .addCase(fetchExpenses.rejected, handleRejected)

      .addCase(fetchExpense.pending, handlePending)
      .addCase(fetchExpense.fulfilled, (state, action) => {
        state.loading = false;
        state.expense = action.payload;
      })
      .addCase(fetchExpense.rejected, handleRejected)

      .addCase(createExpense.pending, handlePending)
      .addCase(createExpense.fulfilled, (state, action) => {
        state.loading = false;
        // The backend may return only { message } (no data) — never push
        // undefined into the list.
        if (action.payload) {
          state.expenses.unshift(action.payload);
        }
      })
      .addCase(createExpense.rejected, handleRejected)

      .addCase(updateExpense.pending, handlePending)
      .addCase(updateExpense.fulfilled, (state, action) => {
        state.loading = false;
        // Guard the payload: a response without a `data` field yields
        // undefined and dereferencing .id would throw inside the reducer.
        if (!action.payload) return;
        const index = state.expenses.findIndex((e: any) => e.id === action.payload.id);
        if (index !== -1) {
          state.expenses[index] = action.payload;
        }
      })
      .addCase(updateExpense.rejected, handleRejected)

      .addCase(deleteExpense.pending, handlePending)
      .addCase(deleteExpense.fulfilled, (state, action) => {
        state.loading = false;
        state.expenses = state.expenses.filter((e: any) => e.id !== action.payload);
      })
      .addCase(deleteExpense.rejected, handleRejected);
  },
});

export const { clearExpense, resetExpenses } = expensesSlice.actions;

export const selectExpenses = (state: any) => state.expenses.expenses;
export const selectExpense = (state: any) => state.expenses.expense;
// M-17 — BC-math accumulation; the float reduce drifted on 4-dp sums.
// Guard each amount: bc-math runs `BigInt(intPart)` on the token, so a
// non-numeric `amount` ('' / 'N/A' / null) threw a RangeError inside the
// reduce and crashed render.
export const selectTotalExpenses = (state: any) =>
  (Array.isArray(state?.expenses?.expenses) ? state.expenses.expenses : []).reduce(
    (sum: string, e: any) => {
      const raw = e?.amount;
      if (raw === null || raw === undefined || raw === '') return sum;
      const numeric = typeof raw === 'number' ? raw : Number(raw);
      if (!Number.isFinite(numeric)) return sum;
      return bcadd(sum, bctoFixed(numeric));
    },
    '0',
  );
export const selectExpensesLoading = (state: any) => state.expenses.loading;
export const selectExpensesError = (state: any) => state.expenses.error;

export default expensesSlice.reducer;
